import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'

const SECTIONS = [
  {
    title: 'Information we collect',
    content: [
      'Account information: email address and password (stored securely via Supabase Auth).',
      'Pantry data: items you add, quantities, expiration dates, locations, and shopping history.',
      'Shopping data: store names, prices, sale information, and receipt scans you choose to upload.',
      'Usage data: how you interact with the app to improve predictions and features.',
      'Device information: device type and operating system for app compatibility.',
    ],
  },
  {
    title: 'How we use your information',
    content: [
      'To power pantry tracking, shopping list generation, and expiry predictions.',
      'To improve AI-generated meal suggestions and ingredient substitutions.',
      'To send your shopping list to your email when auto-generated.',
      'To process payments securely through Stripe for Pantry+ subscriptions.',
      'To provide Alexa voice skill functionality when you link your account.',
      'We never sell your personal data to third parties.',
    ],
  },
  {
    title: 'AI & data processing',
    content: [
      'Pantry uses Claude AI (Anthropic) to enhance barcode scans, generate meal suggestions, analyze receipts, and suggest ingredient substitutions.',
      'Data sent to Claude AI includes item names, pantry contents, and receipt information — never your email or payment details.',
      "Anthropic's privacy policy governs how they handle data: anthropic.com/privacy.",
      'AI processing is used only to provide features you actively use — we do not train models on your personal data.',
    ],
  },
  {
    title: 'Data storage & security',
    content: [
      'Your data is stored securely in Supabase (PostgreSQL) with row-level security policies.',
      "Each household's data is isolated — other users cannot access your pantry.",
      'Passwords are never stored in plain text.',
      'Payment processing is handled entirely by Stripe — we never store card numbers.',
      'All data is transmitted over HTTPS/TLS encryption.',
    ],
  },
  {
    title: 'Third-party services',
    content: [
      'Supabase — database and authentication (supabase.com/privacy)',
      'Stripe — payment processing (stripe.com/privacy)',
      'Anthropic — AI features (anthropic.com/privacy)',
      'Resend — transactional email delivery (resend.com/privacy)',
      'Vercel — app hosting and serverless functions (vercel.com/legal/privacy-policy)',
      'Open Food Facts — barcode product database (world.openfoodfacts.org/terms-of-use)',
      'Sentry — error monitoring. Captures only your user ID, email, and household ID alongside crash reports. Session replays redact all text and block all media, and only fire when an error occurs (sentry.io/privacy).',
      'PostHog — product analytics. Captures only your user ID, email, household ID, and the explicit events we fire (sign-ups, scans, subscriptions, etc.). No automatic page-content capture, no session recording. Hosted on US infrastructure (posthog.com/privacy).',
      'Both Sentry and PostHog are GDPR- and CCPA-compliant.',
    ],
  },
  {
    title: 'Your rights',
    content: [
      'Access: you can view all your data in the app at any time.',
      'Export: contact us to request a full export of your data.',
      'Deletion: delete your account at any time from Settings → Household → Delete account. All data is permanently removed.',
      'Correction: edit any item, price, or household information directly in the app.',
      'California residents (CCPA) and EU residents (GDPR) have additional rights — contact us to exercise them.',
    ],
  },
  {
    title: "Children's privacy",
    content: [
      'Pantry is not directed at children under 13.',
      'We do not knowingly collect personal information from children under 13.',
      'If you believe a child has provided us personal information, contact us immediately.',
    ],
  },
  {
    title: 'Changes to this policy',
    content: [
      'We may update this privacy policy from time to time.',
      'Significant changes will be communicated via email or an in-app notice.',
      'Continued use of Pantry after changes constitutes acceptance of the updated policy.',
      'The "last updated" date at the top of this page reflects the most recent revision.',
    ],
  },
  {
    title: 'Contact us',
    content: [
      'If you have questions about this privacy policy or your data, contact us at:',
      'privacy@pantryioapp.com',
      'We aim to respond to all privacy inquiries within 5 business days.',
    ],
  },
]

export default function Privacy() {
  const navigate = useNavigate()
  useEffect(() => { document.title = 'Privacy Policy — Pantry' }, [])

  return (
    <main className="min-h-screen bg-cream pb-16">
      <div className="max-w-[440px] mx-auto px-5">

        {/* Header */}
        <div className="pt-3 pb-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-1.5 -ml-1.5 rounded-full text-ink"
            aria-label="Back"
          >
            <ChevronLeft className="h-5 w-5" strokeWidth={2} />
          </button>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium">
              Legal
            </p>
            <h1 className="font-serif text-[22px] text-ink leading-[1.15] tracking-[-0.015em] mt-0.5">
              Your <em className="italic">privacy</em>
            </h1>
            <p className="font-serif italic text-[11px] text-ink-muted mt-1">
              Last updated April 2026
            </p>
          </div>
        </div>

        {/* Intro card */}
        <div className="bg-paper border border-border-sand rounded-3xl p-5 mb-5">
          <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
            In short
          </p>
          <p className="font-serif text-[15px] text-ink leading-[1.45]">
            We collect only what makes the app work, we <em className="italic">never</em> sell your data,
            and you can delete everything at any time.
          </p>
        </div>

        {/* Sections */}
        <div className="flex flex-col gap-4">
          {SECTIONS.map((section, i) => (
            <div key={i} className="bg-paper border border-border-sand rounded-2xl overflow-hidden">
              <div className="px-5 py-3.5 border-b border-border-hairline bg-cream-warm/40">
                <h2 className="font-serif text-[14px] italic text-ink">
                  {section.title}
                </h2>
              </div>
              <div className="px-5 py-4 flex flex-col gap-2.5">
                {section.content.map((item, j) => (
                  <div key={j} className="flex items-start gap-2.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-forest flex-shrink-0 mt-2" aria-hidden />
                    <p className="font-serif text-[13px] text-ink leading-[1.6]">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer flourish */}
        <div className="mt-10 text-center">
          <p className="font-serif italic text-[12px] text-ink-muted">
            Pantry — your household memory.
          </p>
          <p className="font-serif text-[11px] text-ink-muted/70 mt-0.5">
            pantryioapp.com
          </p>
        </div>

      </div>
    </main>
  )
}
